import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppFocus - THE KEYBOARD'S PLACE SURVIVES A REPAINT, AND A CLOSED LAYER GIVES IT BACK (audit 2026-10-01).
//
// ⭐ WHAT WAS FOUND, in real Chrome with the keyboard alone: a task ticked with Enter, "Theirs" chosen, Skip pressed on a
// card, the AI sheet closed with Esc - after each one `document.activeElement` was `<body>`. The control that had the
// focus was REPLACED (a list drawn again with `innerHTML`) or HIDDEN (a panel closed), and the browser then drops the
// focus to the top of the document: the next Tab starts at the wordmark, and a screen reader starts reading the page
// from its first line. Nothing errors - the keyboard user is just somewhere else.
//
// ⭐ TWO SMALL TOOLS, no state of their own:
//   `AppFocusKeep.Before(Root)` / `After(Root, Mark)` around a redraw of `Root`: the focused control is described by
//       WHAT IT IS (its tag, id, the task / filter / fold it belongs to, its first class) - never by its position in the
//       old markup - and found again in the new markup. When it is no longer there (a ticked task left the Today strip)
//       the focus goes to the control now standing where it stood (the next task's tick), so the keyboard walks on.
//   `AppFocus.Inside` / `To` / `ToFirst`: "was the focus in this layer?" before it closes, and "put it on its opener".
//
// ⛔ FOCUS ONLY: nothing here clicks, submits or sends (decision 9). ⛔ A control that never had the focus is never given
// it - a mouse user's page does not jump.
//
// Habitat-free: the little of the DOM it reads is an interface, so the suites drive it with a small fake tree, and a
// test double that lacks a member (an older fake) makes every method a no-op rather than a throw.
// A second consumer: anything that redraws a region with `innerHTML` and has controls in it. Nothing here names a
// product, a view or an attribute that is not passed in or listed in `AppFocusKeep.IdentityAttrs`.

/** The little of an element this reads. */
export interface IFocusEl {
    id?: string;
    tagName?: string;
    className?: unknown;
    isConnected?: boolean;
    hidden?: boolean;
    focus?(): void;
    contains?(Other: unknown): boolean;
    getAttribute?(Name: string): string | null;
    hasAttribute?(Name: string): boolean;
    querySelectorAll?(Selector: string): ArrayLike<IFocusEl>;
    querySelector?(Selector: string): IFocusEl | null;
    closest?(Selector: string): IFocusEl | null;
}

/** The little of a document this reads. */
export interface IFocusDoc {
    activeElement?: IFocusEl | null;
    body?: IFocusEl | null;
    getElementById?(Id: string): IFocusEl | null;
}

/** What had the focus inside a region about to be redrawn. */
export class AppFocusMark {
    /** False = the focus was not inside the region: `After` then does nothing. */
    public Had: boolean = false;
    /** What the control IS (`AppFocusKeep.SigOf`). */
    public Sig: string = "";
    /** Its place among the region's controls - the fallback when it is gone. */
    public At: number = -1;
    /** The focus was on the region itself (where the page put it after a route change), not on a control in it. */
    public Self: boolean = false;
}

export class AppFocus {
    /** Is the keyboard's focus inside the element `Id` (or on it)? */
    public static Inside = (Doc: IFocusDoc, Id: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (typeof Doc.getElementById !== "function") { return false; }
            const Box: IFocusEl | null = Doc.getElementById(Id);
            const Active: IFocusEl | null | undefined = Doc.activeElement;
            if (Box === null || Active === null || Active === undefined || typeof Box.contains !== "function") { return false; }
            return Box.contains(Active) === true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFocus.Inside");
            return false;
        }
    };

    /** Is the focus nowhere (on `<body>`, or on nothing)? */
    public static Lost = (Doc: IFocusDoc): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Active: IFocusEl | null | undefined = Doc.activeElement;
            if (Active === null || Active === undefined || Active === Doc.body) { return true; }
            return Active.isConnected === false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFocus.Lost");
            return false;
        }
    };

    /** Focus the element `Id`. True = it was there and was asked to take it. */
    public static To = (Doc: IFocusDoc, Id: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (typeof Doc.getElementById !== "function") { return false; }
            return AppFocus.On(Doc.getElementById(Id));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFocus.To");
            return false;
        }
    };

    /** Focus `El` when it can take it. */
    public static On = (El: IFocusEl | null | undefined): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (El === null || El === undefined || typeof El.focus !== "function") { return false; }
            El.focus();
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFocus.On");
            return false;
        }
    };

    /**
     * Focus a REGION (the page's main area, a pane) - what a route change does when the control that was pressed is not
     * in the new view: a screen reader starts reading at the view, and the next Tab enters it. The region is made
     * focusable by the page only (`tabindex="-1"`: never a Tab stop, and it draws no ring - `app.css`).
     */
    public static Region = (El: IFocusEl | null | undefined): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (El === null || El === undefined || typeof El.focus !== "function") { return false; }
            const Settable: { setAttribute?(Name: string, Value: string): void } = El as unknown as { setAttribute?(Name: string, Value: string): void };
            if (typeof El.hasAttribute === "function" && El.hasAttribute("tabindex") === false && typeof Settable.setAttribute === "function") {
                Settable.setAttribute("tabindex", "-1");
            }
            // ⚠ `preventScroll`: the view was just painted at its top; focusing must not move it.
            (El as unknown as { focus(Options: { preventScroll: boolean }): void }).focus({ preventScroll: true });
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFocus.Region");
            return false;
        }
    };

    /** Focus the first control inside `Box` (a panel that just opened). True = one was there. */
    public static ToFirst = (Box: IFocusEl | null | undefined): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const All: IFocusEl[] = AppFocusKeep.Controls(Box);
            if (All.length === 0) { return false; }
            return AppFocus.On(All[0]);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFocus.ToFirst");
            return false;
        }
    };
}

export class AppFocusKeep {
    /** What can take the focus by Tab. ⚠ A disabled control cannot - which is why a busy control stays enabled-looking
     *  (`aria-disabled`) rather than `disabled` wherever its focus must survive (`AppTaskView.Lead`). */
    public static readonly ControlSelector: string =
        "a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]),"
        + " summary, [tabindex]:not([tabindex=\"-1\"])";

    /** The attributes that say WHICH control this is (never what it would do next - a tick's action changes when pressed). */
    public static readonly IdentityAttrs: string[] = ["href", "data-wac-tid", "data-wac-tfilter", "data-wac-tfold", "data-wac-topen",
        "data-wac-nid", "data-wac-nact", "data-wac-item", "data-wac-i", "data-wac-menu", "for", "name", "value"];

    /** The controls inside `Box`, in document order ([] when the box cannot be searched). */
    public static Controls = (Box: IFocusEl | null | undefined): IFocusEl[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            if (Box === null || Box === undefined || typeof Box.querySelectorAll !== "function") { return []; }
            const Out: IFocusEl[] = [];
            const All: ArrayLike<IFocusEl> = Box.querySelectorAll(AppFocusKeep.ControlSelector);
            for (let At: number = 0; At < All.length; At = At + 1) {
                // ⚠ A control inside a closed <details> or a hidden box is not a place the focus can go.
                if (AppFocusKeep.Shut(All[At], Box) === false) { Out.push(All[At]); }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFocusKeep.Controls");
            return [];
        }
    };

    /** Is `El` inside something hidden (the `hidden` attribute, or a closed `<details>` it is not the summary of)? */
    private static Shut = (El: IFocusEl, Box: IFocusEl): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (typeof El.closest !== "function") { return false; }
            if (El.closest("[hidden]") !== null) { return true; }
            const Fold: IFocusEl | null = El.closest("details:not([open])");
            if (Fold === null) { return false; }
            // The summary of a closed <details> is its one reachable control.
            return !(nz(El.tagName).toUpperCase() === "SUMMARY" && typeof Fold.querySelector === "function" && Fold.querySelector("summary") === El);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFocusKeep.Shut");
            return false;
        }
    };

    /** What `El` is: its tag, id, identity attributes and first class - the same before and after a redraw. */
    public static SigOf = (El: IFocusEl | null | undefined): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (El === null || El === undefined) { return ""; }
            const Out: string[] = [nz(El.tagName).toLowerCase(), nz(El.id)];
            for (const Name of AppFocusKeep.IdentityAttrs) {
                let Value: string = "";
                if (typeof El.getAttribute === "function") { Value = nz(El.getAttribute(Name)); }
                Out.push(Value);
            }
            let First: string = "";
            if (typeof El.className === "string") { First = El.className.trim().split(" ")[0]; }
            Out.push(First);
            return Out.join("|");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFocusKeep.SigOf");
            return "";
        }
    };

    /** Before `Root` is redrawn: what has the focus inside it (nothing = a mark that `After` ignores). */
    public static Before = (Doc: IFocusDoc | null | undefined, Root: IFocusEl | null | undefined): AppFocusMark => {
        const Mark: AppFocusMark = new AppFocusMark();
        if (getCoreUtils().KErrorMode === true) { return Mark; }
        try {
            if (Doc === null || Doc === undefined || Root === null || Root === undefined || typeof Root.contains !== "function") { return Mark; }
            const Active: IFocusEl | null | undefined = Doc.activeElement;
            if (Active === null || Active === undefined || Active === Doc.body || Root.contains(Active) !== true) { return Mark; }
            Mark.Had = true;
            Mark.Self = Active === Root;
            Mark.Sig = AppFocusKeep.SigOf(Active);
            Mark.At = AppFocusKeep.Controls(Root).indexOf(Active);
            return Mark;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFocusKeep.Before");
            return Mark;
        }
    };

    /**
     * After the redraw: the same control again; else - with `Place` - the control now standing where it stood (the last
     * one when the list got shorter); else nothing. True = the focus was put somewhere. ⛔ Never when the focus was not in
     * `Root` before. `Place` false = only the SAME control counts (a route change: another view's third control is not
     * "the same place").
     */
    public static After = (Root: IFocusEl | null | undefined, Mark: AppFocusMark, Place: boolean = true): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Mark.Had !== true) { return false; }
            if (Mark.Self === true) { return AppFocus.Region(Root); }
            const All: IFocusEl[] = AppFocusKeep.Controls(Root);
            if (All.length === 0) { return false; }
            for (const One of All) {
                if (AppFocusKeep.SigOf(One) === Mark.Sig) { return AppFocus.On(One); }
            }
            if (Place !== true) { return false; }
            let At: number = Mark.At;
            if (At < 0) { At = 0; }
            if (At >= All.length) { At = All.length - 1; }
            return AppFocus.On(All[At]);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFocusKeep.After");
            return false;
        }
    };

    /**
     * ⭐ REPLACE `Root`'s markup AND KEEP THE KEYBOARD'S PLACE IN IT - `Before`, the write, `After`, in one step (the
     * three-pane tabs line and the translation editor). The caller decides WHETHER to write (each has its own "did it
     * change" rule); `Place` is `After`'s.
     */
    public static Swap = (Doc: IFocusDoc | null | undefined, Root: IFocusEl & { innerHTML: string }, Html: string,
                          Place: boolean = true): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Kept: AppFocusMark = AppFocusKeep.Before(Doc, Root);
            Root.innerHTML = Html;
            AppFocusKeep.After(Root, Kept, Place);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFocusKeep.Swap");
        }
    };
}
